import type { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router'
import localSave from '@utils/local-save'
import postAuthRedirect from '@utils/post-auth-redirect'
import { AUTH_ROUTES } from '@router/auth-routes'
import { useAuthStore } from '@stores/auth'
import { useStatusStore } from '@stores/status'
import { trimTrailingSlash } from '@utils/urls'

export function createNavigationGuard() {
    return function navigationGuard(
        routeTo: RouteLocationNormalized,
        routeFrom: RouteLocationNormalized
    ): NavigationGuardReturn | Promise<NavigationGuardReturn> {
        // Returning a location restarts the guard chain against the trimmed path,
        // where this test is false — so the redirect settles on the second pass
        // instead of looping.
        if (routeTo.path !== '/' && routeTo.path.endsWith('/')) {
            return { path: trimTrailingSlash(routeTo.path) }
        }

        const statusStore = useStatusStore()
        statusStore.setHistoryLength(statusStore.historyLength + 1)

        // removed 'home' exception due to redirecting issue from various pages
        if (!AUTH_ROUTES.includes(routeTo.name as string)) {
            localSave.save('redirectURL', routeTo.path)
        }

        postAuthRedirect.clearOnAuthFlowExit(routeFrom, routeTo)

        const authRequired = routeTo.matched.some(route => route.meta.authRequired)
        const login = { name: 'login', query: { redirectFrom: routeTo.fullPath } }

        if (authRequired) {
            // The guard must ALWAYS resolve the navigation — a swallowed
            // failure here is exactly what used to dead-end the whole app.
            return useAuthStore().validate({ redirectTo: routeTo.fullPath })
                .then(validUser => (validUser ? true : login))
                .catch(() => login)
        }

        return true
    }
}
